import React from 'react';
import { Localizer } from 'services/Localizer';
import Spinner from 'shared/components/Spinner';
import InlineAlert from 'shared/edit/InlineAlert';
import { TextBoxField, CheckBoxField } from 'shared/edit/fields';
import RocForm from 'shared/edit/RocForm';
import ReCaptchaDisclaimer from 'shared/edit/fields/ReCaptchaDisclaimer';
import { ServerData } from 'services/ServerData';
import AccountUrls from 'shared/util/AccountUrls';
import { getQueryParam } from 'shared/util/Misc';
import ReSendAccountConfirmationButton from './ReSendAccountConfirmationButton';
import useLogin, { LoginModel } from './useLogin';
import ExternalLogins from './ExternalLogins';

export default function Login() {
	const loginSubmitter = useLogin();
	const returnUrl = getQueryParam('ReturnUrl', true);
	const accountLocked = getQueryParam('locked');

	if (loginSubmitter.submitted && loginSubmitter.responseData && loginSubmitter.responseData.success) {
		if (loginSubmitter.responseData.requiresTwoFactor) {
			const rememberMe = loginSubmitter.responseData.rememberMe || false;
			window.location.href =
				AccountUrls.Login2fa +
				(returnUrl != null
					? '?ReturnUrl=' + encodeURIComponent(returnUrl) + '&RememberMe=' + rememberMe
					: '?RememberMe=' + rememberMe);
		} else if (returnUrl) {
			window.location.href = returnUrl;
		} else {
			window.location.href = '/my-account';
		}

		return null;
	}

	return (
		<div className="roc-body-content__row">
			<div className="roc-body-content__left">
				<h1>{Localizer('LoginPageTitleText')}</h1>
				<RocForm<LoginModel>
					onSubmit={loginSubmitter.handleSubmit}
					render={({ handleSubmit, submitError, submitting }) => {
						return (
							<form className="roc-form roc-register-form" onSubmit={handleSubmit}>
								{submitError || accountLocked ? (
									<InlineAlert
										title={Localizer('ErrorMessageGenericTitle')}
										// Prioritize form errors over external login errors
										message={submitError ?? Localizer('UserAccountLockedOut')}
										error={null}
									/>
								) : null}

								<TextBoxField
									isRequired
									label={
										ServerData.USE_EMAIL_AS_USERNAME ? Localizer('Email') : Localizer('Username')
									}
									name={ServerData.USE_EMAIL_AS_USERNAME ? 'email' : 'userName'}
									testId="login-username"
								/>

								<ReSendAccountConfirmationButton />

								<TextBoxField
									isRequired
									type="password"
									label={Localizer('Password')}
									name="password"
									testId="login-password"
								/>

								<CheckBoxField name="rememberMe" label={Localizer('RememberMe')} />

								<ReCaptchaDisclaimer />

								<button
									disabled={submitting}
									type="submit"
									color="secondary"
									className="roc-btn"
									data-testid="login-submit"
								>
									{Localizer('Login')} {submitting ? <Spinner light /> : null}
								</button>

								<div className="roc-login-form__footer">
									<div className="roc-form-group">
										<p>
											<a href={AccountUrls.ForgotPassword} data-testid="forgot-your-password">
												{Localizer('ForgotYourPassword')}
											</a>
										</p>
										<p>
											<a
												href={
													AccountUrls.Register +
													(returnUrl != null
														? '?ReturnUrl=' + encodeURIComponent(returnUrl ?? '')
														: '')
												}
												data-testid="register-as-new-user"
											>
												{Localizer('RegisterNewUserLinkText')}
											</a>
										</p>
									</div>
								</div>
							</form>
						);
					}}
				/>
			</div>
			<div className="roc-body-content__right">
				<ExternalLogins externalLoginProviders={ServerData.EXTERNAL_LOGIN_PROVIDERS} />
			</div>
		</div>
	);
}
